import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Laptop, CheckCircle, BookOpen, Clock, ArrowRight, FileCheck, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

// Check if promo is active (expires 2/28/2026)
const isPromoActive = () => {
  const now = new Date();
  const promoEnd = new Date('2026-02-28T23:59:59');
  return now <= promoEnd;
};

const pricingData = [
  { course: 'Heartsaver CPR/AED', price: '$75', promo: '$60' },
  { course: 'Heartsaver First Aid', price: '$70', promo: '$55' },
  { course: 'CPR/AED & First Aid', price: '$100', promo: '$85' },
  { course: 'BLS for Healthcare Providers', price: '$80', promo: '$65' },
  { course: 'BLS AND First Aid', price: '$110', promo: '$95' },
  { course: 'Blood Borne Pathogens', price: '$40', promo: '$25' },
  { course: 'K-12 CPR/AED & First Aid (Student Deal)', price: '$40', promo: '$40', highlight: true },
  { course: 'Family & Friends CPR', price: '$10', promo: '$10' },
];

const howItWorks = [
  {
    step: '1',
    title: 'Complete Online Portion',
    description: 'Complete the online learning modules at your own pace from home.',
  },
  {
    step: '2',
    title: 'Schedule Skills Check',
    description: 'Contact us to schedule your in-person skills session.',
  },
  {
    step: '3',
    title: 'Get Certified',
    description: 'Complete hands-on skills check and receive your certification.',
  },
];

const whatToBring = [
  'Proof of completed online portion',
  'Valid photo ID',
  'Comfortable clothing for hands-on practice',
  'Questions about the material',
];

export default function BlendedCourses() {
  const showPromo = isPromoActive();
  
  return (
    <div className="min-h-screen bg-white">
      {/* Promo Banner */}
      {showPromo && (
        <div className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white py-3 px-4 text-center">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Tag className="w-5 h-5" />
            <span className="font-bold">LIMITED TIME: $15 OFF All Blended Courses!</span>
            <Badge variant="secondary" className="bg-white/20 text-white">Expires 2/28/2026</Badge>
          </div>
        </div>
      )}
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EDF6F9] via-white to-[#83C5BE]/20 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#83C5BE]/20 text-[#006D77] text-sm font-medium mb-6">
              <Laptop className="w-4 h-4" />
              <span>Online + In-Person</span>
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#264653] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="blended-courses-title"
            >
              Blended Courses
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Learn at your own pace online, then complete a quick in-person skills check 
              to earn your certification. The best of both worlds.
            </p>
            <Link to="/book-now">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg"
                data-testid="book-blended-btn"
              >
                Book Blended Course
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              How Blended Courses Work
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple three-step process to get certified on your schedule
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full text-center border-slate-100 hover:shadow-lg transition-all" data-testid={`how-it-works-step-${index}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                      {item.step}
                    </div>
                    <h3 
                      className="text-xl font-semibold text-[#264653] mb-3"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-16 md:py-24 bg-[#EDF6F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Blended Course Pricing
            </h2>
            <p className="text-muted-foreground">Skills check pricing after completing online portion</p>
            {showPromo && (
              <Badge className="mt-4 bg-[#c31c31] text-white text-base px-4 py-1">$15 OFF All Courses!</Badge>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="max-w-2xl mx-auto border-slate-100 overflow-hidden" data-testid="blended-pricing-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5">
                    <TableHead className="text-[#264653] font-semibold text-base">Course</TableHead>
                    <TableHead className="text-right text-[#264653] font-semibold text-base">
                      {showPromo ? 'Sale Price' : 'Price'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingData.map((item, index) => (
                    <TableRow key={index} className={`hover:bg-primary/5 ${item.highlight ? 'bg-gradient-to-r from-red-50 to-purple-50' : ''}`}>
                      <TableCell className="font-medium text-[#264653]">
                        <div className="flex items-center gap-2">
                          {item.course}
                          {item.highlight && <Badge className="bg-[#c31c31] text-white text-xs">Student Deal ❤️</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {showPromo ? (
                          <div className="flex items-center justify-end gap-2">
                            {item.price !== item.promo && <span className="text-slate-400 line-through text-sm">{item.price}</span>}
                            <span className="text-[#c31c31] font-bold text-lg">{item.promo}</span>
                          </div>
                        ) : (
                          <span className="text-primary font-bold text-lg">{item.price}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </motion.div>
          <div className="text-center mt-10">
            <Link to="/book-now">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg"
                data-testid="book-blended-from-pricing-btn"
              >
                Book Blended Course
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#264653] mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                What to Bring to Your Skills Check
              </h2>
              <ul className="space-y-4">
                {whatToBring.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="benefit-card-1">
                <CardContent className="p-0">
                  <Clock className="w-8 h-8 text-primary mb-3" />
                  <p className="font-medium text-[#264653]">Skills Check: 30-60 min</p>
                </CardContent>
              </Card>
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="benefit-card-2">
                <CardContent className="p-0">
                  <BookOpen className="w-8 h-8 text-[#83C5BE] mb-3" />
                  <p className="font-medium text-[#264653]">Learn at Your Own Pace</p>
                </CardContent>
              </Card>
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="benefit-card-3">
                <CardContent className="p-0">
                  <FileCheck className="w-8 h-8 text-[#E29578] mb-3" />
                  <p className="font-medium text-[#264653]">Same Certification</p>
                </CardContent>
              </Card>
              <Card className="p-6 bg-[#EDF6F9] border-0" data-testid="benefit-card-4">
                <CardContent className="p-0">
                  <Laptop className="w-8 h-8 text-primary mb-3" />
                  <p className="font-medium text-[#264653]">Flexible Scheduling</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
